import { DOMAIN_RESOURCE_TYPES } from './domainResourceTypes';
import type { DomainResourceElement, DomainResourceType, DomainResourceTypeKey } from './domainResourceTypes';
import type {
  OrganizationDomainResources,
  OrganizationDomainTeardownRequest,
} from '../../../types/models/OrganizationDomainResources';

/**
 * Selection state and pure transitions of the "Delete resources" wizard.
 *
 * Invariants (CLAUDE.md → "Organization-domain linked resources"; plan §5.2), every one of them kept
 * in a SINGLE returned value by every transition — a checkbox is never merely disabled while left
 * checked:
 *  (a) the organization domain may only be selected when every registered type is satisfied,
 *      `under_development` types included (an unfinished type makes the domain undeletable by design);
 *  (b) a type is satisfied when every element the user COULD delete is selected — elements the backend
 *      cannot delete ("forced-kept") never block; keeping any deletable element by choice does. One
 *      exception: the mail-domain record always has to go (FK `ON DELETE RESTRICT` on the org domain);
 *  (c) the mail-domain record is a DERIVED element: selected iff every deletable mailbox and alias is
 *      selected — keeping any of them keeps the mail domain;
 *  (d) a type with zero elements is vacuously satisfied ("Nothing hosted").
 *
 * Every function is pure and takes the registry as an argument (defaulting to the real one) so the
 * behaviour can be unit-tested with synthetic types.
 */

export interface DeleteResourcesSelection {
  /** Types ticked in step 1. */
  readonly selectedTypes: ReadonlySet<DomainResourceTypeKey>;
  /** Element keys chosen for deletion, per type (derived elements included once their dependencies are met). */
  readonly selectedElements: ReadonlyMap<DomainResourceTypeKey, ReadonlySet<string>>;
  readonly deleteOrgDomain: boolean;
}

export const EMPTY_WIZARD_SELECTION: DeleteResourcesSelection = {
  selectedTypes: new Set<DomainResourceTypeKey>(),
  selectedElements: new Map<DomainResourceTypeKey, ReadonlySet<string>>(),
  deleteOrgDomain: false,
};

/** The aggregate read projected through every registered type's `elementsOf`. */
export interface ResourceInventory {
  readonly elements: ReadonlyMap<DomainResourceTypeKey, readonly DomainResourceElement[]>;
}

export const buildInventory = (
  resources: OrganizationDomainResources,
  types: readonly DomainResourceType[] = DOMAIN_RESOURCE_TYPES,
): ResourceInventory => ({
  elements: new Map(types.map((type) => [type.key, type.elementsOf(resources)] as const)),
});

const elementsOf = (inventory: ResourceInventory, key: DomainResourceTypeKey): readonly DomainResourceElement[] =>
  inventory.elements.get(key) ?? [];

const selectedOf = (selection: DeleteResourcesSelection, key: DomainResourceTypeKey): ReadonlySet<string> =>
  selection.selectedElements.get(key) ?? new Set<string>();

const typeByKey = (types: readonly DomainResourceType[], key: DomainResourceTypeKey): DomainResourceType => {
  const type = types.find((candidate) => candidate.key === key);
  if (type === undefined) {
    throw new Error(`Unknown resource type "${key}"`);
  }
  return type;
};

/** Deletable elements the user ticks directly (derived ones are computed, never ticked). */
const tickable = (elements: readonly DomainResourceElement[]): DomainResourceElement[] =>
  elements.filter((element) => element.deletable && !element.derived);

// ---------------------------------------------------------------------------------------------
// Classification
// ---------------------------------------------------------------------------------------------

/**
 * How a type presents in step 1:
 *  - `under_development`: row disabled with the visible reason; never satisfied (a).
 *  - `nothing_hosted`: no element; row disabled "Nothing hosted"; vacuously satisfied (d); no step.
 *  - `all_forced_kept`: elements exist but none is deletable; row disabled with the blocked reason;
 *    satisfied (b); no step; everything listed under *Will be kept*.
 *  - `selectable`: at least one deletable element (a derived one counts — a mail domain with no
 *    mailboxes is deletable through its record); row enabled.
 */
export type TypeState = 'under_development' | 'nothing_hosted' | 'all_forced_kept' | 'selectable';

export const typeState = (inventory: ResourceInventory, type: DomainResourceType): TypeState => {
  if (type.status === 'under_development') {
    return 'under_development';
  }
  const elements = elementsOf(inventory, type.key);
  if (elements.length === 0) {
    return 'nothing_hosted';
  }
  return elements.some((element) => element.deletable) ? 'selectable' : 'all_forced_kept';
};

// ---------------------------------------------------------------------------------------------
// Predicates
// ---------------------------------------------------------------------------------------------

/**
 * (b)/(d), uniform for atomic and per-element types: every deletable element is selected —
 * vacuously true when nothing is hosted or nothing is deletable. `under_development` ⇒ false (a).
 */
export const typeSatisfied = (
  selection: DeleteResourcesSelection,
  inventory: ResourceInventory,
  type: DomainResourceType,
): boolean => {
  if (type.status === 'under_development') {
    return false;
  }
  const selected = selectedOf(selection, type.key);
  return elementsOf(inventory, type.key)
    .filter((element) => element.deletable)
    .every((element) => selected.has(element.key));
};

/**
 * Whether a derived element of `key` goes: its type is selected, it is deletable, and every tickable
 * element of the type is selected. (c) for the mail-domain record.
 */
const derivedGoes = (
  selection: DeleteResourcesSelection,
  inventory: ResourceInventory,
  key: DomainResourceTypeKey,
  derived: DomainResourceElement,
): boolean => {
  if (!derived.deletable || !selection.selectedTypes.has(key)) {
    return false;
  }
  const selected = selectedOf(selection, key);
  return tickable(elementsOf(inventory, key)).every((element) => selected.has(element.key));
};

/** (c): the mail-domain record goes. False when no mail domain is hosted. */
export const mailDomainGoes = (selection: DeleteResourcesSelection, inventory: ResourceInventory): boolean => {
  const record = elementsOf(inventory, 'email_hosting').find((element) => element.kind === 'mail_domain');
  return record !== undefined && derivedGoes(selection, inventory, 'email_hosting', record);
};

/**
 * (a): every registered type satisfied — plus the one exception to (b): `mail_domain.organization_domain_id`
 * references the organization domain with `ON DELETE RESTRICT`, so the mail-domain record must actually go
 * (a forced-kept record, e.g. an unreachable host, blocks the organization domain even though other
 * forced-kept elements never do). The backend pre-flight enforces the same rule.
 */
export const orgDomainSelectable = (
  selection: DeleteResourcesSelection,
  inventory: ResourceInventory,
  types: readonly DomainResourceType[] = DOMAIN_RESOURCE_TYPES,
): boolean => {
  if (!types.every((type) => typeSatisfied(selection, inventory, type))) {
    return false;
  }
  const record = elementsOf(inventory, 'email_hosting').find((element) => element.kind === 'mail_domain');
  return record === undefined || derivedGoes(selection, inventory, 'email_hosting', record);
};

/**
 * Why the organization domain cannot be selected, in registry order — the review step turns these into
 * the checkbox's helper text. Empty when it *can* be selected.
 */
export const orgDomainBlockers = (
  selection: DeleteResourcesSelection,
  inventory: ResourceInventory,
  types: readonly DomainResourceType[] = DOMAIN_RESOURCE_TYPES,
): string[] => {
  const blockers: string[] = [];
  for (const type of types) {
    if (typeSatisfied(selection, inventory, type)) {
      continue;
    }
    if (type.status === 'under_development') {
      blockers.push(`${type.label} deletion is under development`);
      continue;
    }
    const selected = selectedOf(selection, type.key);
    // Derived elements are excluded: the mail-domain record gets its own line below, and counting it
    // here would report the same thing twice.
    const kept = elementsOf(inventory, type.key).filter(
      (element) => element.deletable && !element.derived && !selected.has(element.key),
    ).length;
    blockers.push(
      `${kept} ${kept === 1 ? 'item' : 'items'} of ${type.label.toLowerCase()} ${kept === 1 ? 'is' : 'are'} kept`,
    );
  }
  // The FK exception: the mail-domain record itself has to go (ON DELETE RESTRICT).
  const record = elementsOf(inventory, 'email_hosting').find((element) => element.kind === 'mail_domain');
  if (record !== undefined && !derivedGoes(selection, inventory, 'email_hosting', record)) {
    const reason =
      record.deletable === false && record.blockedReason !== null
        ? record.blockedReason
        : 'the mail domain record is kept';
    if (!blockers.includes(reason)) {
      blockers.push(reason);
    }
  }
  return blockers;
};

/** At least one element (or the organization domain) is actually going — a selection of only kept elements is not a run. */
export const hasAnyAction = (selection: DeleteResourcesSelection, inventory: ResourceInventory): boolean => {
  if (selection.deleteOrgDomain) {
    return true;
  }
  let found = false;
  selection.selectedElements.forEach((selected, key) => {
    if (found || !selection.selectedTypes.has(key)) {
      return;
    }
    found = elementsOf(inventory, key).some((element) => element.deletable && selected.has(element.key));
  });
  return found;
};

/**
 * Cross-type rules the backend's pre-flight enforces, mirrored client-side so a selection that is
 * certain to be rejected can be caught before it is sent (plan §4.3).
 *
 * Today there is one: the registration record cannot be removed while its domain is still bound to a
 * DNS endpoint, so deleting the registration requires deleting the DNS zone in the same run.
 */
export const crossTypeViolations = (
  selection: DeleteResourcesSelection,
  inventory: ResourceInventory,
  resources: OrganizationDomainResources,
): string[] => {
  const violations: string[] = [];
  const goes = (key: DomainResourceTypeKey): boolean => {
    if (!selection.selectedTypes.has(key)) {
      return false;
    }
    const selected = selectedOf(selection, key);
    return elementsOf(inventory, key).some((element) => element.deletable && selected.has(element.key));
  };
  // `resources.dns_zone` is non-null only while the domain is bound to an endpoint.
  if (goes('domain_registration') && resources.dns_zone !== null && !goes('dns_zone')) {
    violations.push('The DNS zone has to be deleted in the same run before the registration record can be removed.');
  }
  return violations;
};

/** State of the step-1 "Select all resources" box: `null` = indeterminate. `false` when nothing is selectable. */
export const selectAllState = (
  selection: DeleteResourcesSelection,
  inventory: ResourceInventory,
  types: readonly DomainResourceType[] = DOMAIN_RESOURCE_TYPES,
): boolean | null => {
  const selectable = types.filter((type) => typeState(inventory, type) === 'selectable');
  if (selectable.length === 0) {
    return false;
  }
  const count = selectable.filter((type) => selection.selectedTypes.has(type.key)).length;
  if (count === 0) {
    return false;
  }
  return count === selectable.length ? true : null;
};

// ---------------------------------------------------------------------------------------------
// Reconciliation — applied by EVERY transition
// ---------------------------------------------------------------------------------------------

const withElements = (
  selection: DeleteResourcesSelection,
  key: DomainResourceTypeKey,
  selected: ReadonlySet<string>,
): DeleteResourcesSelection => {
  const selectedElements = new Map(selection.selectedElements);
  selectedElements.set(key, selected);
  return { ...selection, selectedElements };
};

/** Sets/clears every derived element from its dependencies, then applies (a) to the org-domain flag. */
export const reconcile = (
  selection: DeleteResourcesSelection,
  inventory: ResourceInventory,
  types: readonly DomainResourceType[] = DOMAIN_RESOURCE_TYPES,
): DeleteResourcesSelection => {
  let next = selection;
  for (const type of types) {
    const derivedElements = elementsOf(inventory, type.key).filter((element) => element.derived);
    if (derivedElements.length === 0) {
      continue;
    }
    const selected = new Set(selectedOf(next, type.key));
    for (const derived of derivedElements) {
      if (derivedGoes(next, inventory, type.key, derived)) {
        selected.add(derived.key);
      } else {
        selected.delete(derived.key);
      }
    }
    next = withElements(next, type.key, selected);
  }
  return { ...next, deleteOrgDomain: next.deleteOrgDomain && orgDomainSelectable(next, inventory, types) };
};

/**
 * After a refetch (retry of a partial run, token-renew refetch): drop types that are no longer
 * selectable and element keys that vanished or stopped being deletable, then reconcile.
 */
export const reconcileInventory = (
  selection: DeleteResourcesSelection,
  inventory: ResourceInventory,
  types: readonly DomainResourceType[] = DOMAIN_RESOURCE_TYPES,
): DeleteResourcesSelection => {
  const selectedTypes = new Set<DomainResourceTypeKey>();
  const selectedElements = new Map<DomainResourceTypeKey, ReadonlySet<string>>();
  for (const type of types) {
    if (!selection.selectedTypes.has(type.key) || typeState(inventory, type) !== 'selectable') {
      continue;
    }
    selectedTypes.add(type.key);
    const stillThere = new Set(
      tickable(elementsOf(inventory, type.key))
        .map((element) => element.key)
        .filter((key) => selectedOf(selection, type.key).has(key)),
    );
    selectedElements.set(type.key, stillThere);
  }
  return reconcile({ selectedTypes, selectedElements, deleteOrgDomain: selection.deleteOrgDomain }, inventory, types);
};

// ---------------------------------------------------------------------------------------------
// Transitions
// ---------------------------------------------------------------------------------------------

/** Tick/untick a type in step 1. Ticking selects every tickable element; unticking clears them. */
export const toggleType = (
  selection: DeleteResourcesSelection,
  inventory: ResourceInventory,
  types: readonly DomainResourceType[],
  key: DomainResourceTypeKey,
  checked: boolean,
): DeleteResourcesSelection => {
  if (typeState(inventory, typeByKey(types, key)) !== 'selectable') {
    return selection;
  }
  const selectedTypes = new Set(selection.selectedTypes);
  const selected = checked
    ? new Set(tickable(elementsOf(inventory, key)).map((element) => element.key))
    : new Set<string>();
  if (checked) {
    selectedTypes.add(key);
  } else {
    selectedTypes.delete(key);
  }
  return reconcile(withElements({ ...selection, selectedTypes }, key, selected), inventory, types);
};

/** "Select all resources": affects only `selectable` types. */
export const toggleAllTypes = (
  selection: DeleteResourcesSelection,
  inventory: ResourceInventory,
  types: readonly DomainResourceType[],
  checked: boolean,
): DeleteResourcesSelection =>
  types
    .filter((type) => typeState(inventory, type) === 'selectable')
    .reduce((next, type) => toggleType(next, inventory, types, type.key, checked), selection);

/** Tick/untick one element inside a selected type. Non-deletable and derived elements are ignored. */
export const toggleElement = (
  selection: DeleteResourcesSelection,
  inventory: ResourceInventory,
  types: readonly DomainResourceType[],
  key: DomainResourceTypeKey,
  elementKey: string,
  checked: boolean,
): DeleteResourcesSelection => {
  const element = elementsOf(inventory, key).find((candidate) => candidate.key === elementKey);
  if (element === undefined || !element.deletable || element.derived || !selection.selectedTypes.has(key)) {
    return selection;
  }
  const selected = new Set(selectedOf(selection, key));
  if (checked) {
    selected.add(elementKey);
  } else {
    selected.delete(elementKey);
  }
  return reconcile(withElements(selection, key, selected), inventory, types);
};

/** Tick/untick every tickable element of a selected type (the step's header checkbox). */
export const toggleAllElements = (
  selection: DeleteResourcesSelection,
  inventory: ResourceInventory,
  types: readonly DomainResourceType[],
  key: DomainResourceTypeKey,
  checked: boolean,
): DeleteResourcesSelection => {
  if (!selection.selectedTypes.has(key)) {
    return selection;
  }
  const selected = checked
    ? new Set(tickable(elementsOf(inventory, key)).map((element) => element.key))
    : new Set<string>();
  return reconcile(withElements(selection, key, selected), inventory, types);
};

/** Tick/untick the organization domain. Ticking is refused unless (a) holds. */
export const toggleOrgDomain = (
  selection: DeleteResourcesSelection,
  inventory: ResourceInventory,
  types: readonly DomainResourceType[],
  checked: boolean,
): DeleteResourcesSelection => ({
  ...selection,
  deleteOrgDomain: checked && orgDomainSelectable(selection, inventory, types),
});

// ---------------------------------------------------------------------------------------------
// Review derivation and request mapping
// ---------------------------------------------------------------------------------------------

export type KeptReason = 'type_unselected' | 'kept_by_choice' | 'forced_kept' | 'under_development' | 'depends_on_kept';

export interface KeptElement {
  element: DomainResourceElement;
  why: KeptReason;
}

export interface ReviewSummary {
  willDelete: ReadonlyMap<DomainResourceTypeKey, readonly DomainResourceElement[]>;
  willKeep: ReadonlyMap<DomainResourceTypeKey, readonly KeptElement[]>;
  nothingToDelete: readonly DomainResourceTypeKey[];
  mailDomainGoes: boolean;
  dnsZoneGoes: boolean;
  orgDomainGoes: boolean;
  /** One consequence sentence per type with kept elements, in registry order. */
  consequences: readonly string[];
}

export const deriveReview = (
  selection: DeleteResourcesSelection,
  inventory: ResourceInventory,
  resources: OrganizationDomainResources,
  types: readonly DomainResourceType[] = DOMAIN_RESOURCE_TYPES,
): ReviewSummary => {
  const willDelete = new Map<DomainResourceTypeKey, readonly DomainResourceElement[]>();
  const willKeep = new Map<DomainResourceTypeKey, readonly KeptElement[]>();
  const nothingToDelete: DomainResourceTypeKey[] = [];

  for (const type of types) {
    const elements = elementsOf(inventory, type.key);
    const state = typeState(inventory, type);
    if (state === 'nothing_hosted') {
      nothingToDelete.push(type.key);
      willDelete.set(type.key, []);
      willKeep.set(type.key, []);
      continue;
    }
    const selected = selectedOf(selection, type.key);
    const typeSelected = selection.selectedTypes.has(type.key);
    const going: DomainResourceElement[] = [];
    const kept: KeptElement[] = [];
    for (const element of elements) {
      if (!element.deletable) {
        kept.push({ element, why: 'forced_kept' });
      } else if (state === 'under_development') {
        kept.push({ element, why: 'under_development' });
      } else if (!typeSelected) {
        kept.push({ element, why: 'type_unselected' });
      } else if (selected.has(element.key)) {
        going.push(element);
      } else {
        kept.push({ element, why: element.derived ? 'depends_on_kept' : 'kept_by_choice' });
      }
    }
    willDelete.set(type.key, going);
    willKeep.set(type.key, kept);
  }

  const dnsElement = elementsOf(inventory, 'dns_zone')[0];
  const dnsZoneGoes =
    dnsElement !== undefined &&
    selection.selectedTypes.has('dns_zone') &&
    selectedOf(selection, 'dns_zone').has(dnsElement.key);

  const consequences: string[] = [];
  for (const type of types) {
    const kept = (willKeep.get(type.key) ?? []).map((entry) => entry.element);
    if (kept.length === 0) {
      continue;
    }
    const sentence = type.keepConsequence({
      domainName: resources.organization_domain.name,
      kept,
      all: elementsOf(inventory, type.key),
      dnsZoneGoes,
      resources,
    });
    if (sentence !== null) {
      consequences.push(sentence);
    }
  }

  return {
    willDelete,
    willKeep,
    nothingToDelete,
    mailDomainGoes: mailDomainGoes(selection, inventory),
    dnsZoneGoes,
    orgDomainGoes: selection.deleteOrgDomain,
    consequences,
  };
};

/** Map the selection to the `POST /organization/domains/{id}/teardown` body (plan §4.3). */
export const toTeardownRequest = (
  selection: DeleteResourcesSelection,
  inventory: ResourceInventory,
  confirmName: string,
): OrganizationDomainTeardownRequest => {
  const goingOf = (key: DomainResourceTypeKey): DomainResourceElement[] => {
    if (!selection.selectedTypes.has(key)) {
      return [];
    }
    const selected = selectedOf(selection, key);
    return elementsOf(inventory, key).filter((element) => element.deletable && selected.has(element.key));
  };
  const email = goingOf('email_hosting');
  return {
    confirm_name: confirmName,
    selection: {
      dns_zone: { delete: goingOf('dns_zone').length > 0 },
      domain_registration: { delete: goingOf('domain_registration').length > 0 },
      email_hosting: {
        mailbox_ids: email.filter((element) => element.kind === 'mailbox').map((element) => element.id),
        alias_ids: email.filter((element) => element.kind === 'alias').map((element) => element.id),
        delete_mail_domain: email.some((element) => element.kind === 'mail_domain'),
      },
      tls_certificates: { certificate_ids: goingOf('tls_certificates').map((element) => element.id) },
      organization_domain: { delete: selection.deleteOrgDomain },
    },
  };
};
